<template>
  <div
    :class="['cf-modifier-groups__group', { 'cf-modifier-groups__group--invalid': showInvalidState && !validateSelectedPortionType() }]"
  >
    <div class="cf-flex cf-flex--column cf-flex--gap-16">
      <div class="cf-flex cf-flex--row cf-flex--justify-space-between">
        <!-- Modifier Name -->
        <h3 class="cf-title cf-title--3">{{ label }}</h3>

        <!-- Modifier Requirements -->
        <h5 class="cf-title cf-title--5">Required</h5>
      </div>

      <div
        v-if="portionTypes.length > 0"
        class="cf-flex cf-flex--row cf-flex--gap-8"
      >
        <!-- NOTE: If we don't have any portionTypes, that means this group's modifiers were misconfigured (missing valid modifierTemplate values) -->
        <button
          v-for="type in portionTypes"
          :key="type.modifierTemplate"
          type="button"
          :class="['cf-button cf-button--secondary cf-button--lg cf-button--auto cf-button--square',{
            'cf-button--tertiary': selectedPortionType && selectedPortionType.modifierTemplate === type.modifierTemplate,
          }]"
          @click="selectPortionType(type)"
        >
          <template v-if="type.modifierTemplate === 'FullGroupTemplate'"><svg-icon icon-name="pizza-full" /> Whole</template>
          <template v-if="type.modifierTemplate === 'HalfGroupTemplate'"><svg-icon icon-name="pizza-half" /> Halves</template>
          <template v-if="type.modifierTemplate === 'ThirdGroupTemplate'"><svg-icon icon-name="pizza-thirds" /> Thirds</template>
          <template v-if="type.modifierTemplate === 'QuarterGroupTemplate'"><svg-icon icon-name="pizza-quarters" /> Quarters</template>
        </button>
      </div>
    </div>

    <div
      v-if="selectedPortionType"
      class="cf-checkbox-group cf-checkbox-group--radio"
    >
      <div class="cf-checkbox-group__items">
        <div
          v-for="modifier in selectedPortionType.modifiers"
          :key="modifier.menuItemId"
          class="cf-checkbox-group__item cf-checkbox-group__item--arrow"
          :class="{
            'cf-checkbox-group__item--invalid': showInvalidState && !validatePortion(modifier),
            'cf-checkbox-group__item--selected': selections[modifier.menuItemId]
          }"
        >
          <button
            type="button"
            class="cf-checkbox-group__label"
            :aria-invalid="!validatePortion(modifier)"
            @click="editPortion(modifier)"
          >
            <div
              v-if="modifier.images && modifier.images[0]"
              class="cf-checkbox-group__label-image"
              :style="`background-image: url(${ modifier.images[0].url })`"
            />

            <div class="cf-checkbox-group__label-content">
              <p class="cf-checkbox-group__label-title">{{ modifier.displayName }}</p>

              <p
                v-if="selections[modifier.menuItemId]"
                class="cf-subtitle cf-subtitle--2"
              >
                {{ returnSelectedModifiers(modifier, selections[modifier.menuItemId]).selectionDisplay }}
              </p>

              <p
                v-else
                class="cf-subtitle cf-subtitle--2"
              >
                Select to customize...
              </p>
            </div>

            <span
              v-if="!hidePrices && returnSelectedModifiers(modifier, selections[modifier.menuItemId]).totalPrice > 0"
              class="cf-checkbox-group__label-description"
            >
              +{{ $dollars(returnSelectedModifiers(modifier, selections[modifier.menuItemId]).totalPrice) }}
            </span>
          </button>
        </div>
      </div>
    </div>


    <modal
      v-slot="{ data }"
      :name="modalName"
      :display-header-title="false"
      :has-back-arrow="true"
    >
      <cf-modifier-menu-item-child
        :item="data.item"
        :read-only="readOnly"
        :selected-modifiers="data.selectedModifiers"
        :selected-quantity="data.selectedQuantity"
        @close="$closeModal(modalName)"
        @update="updatedChildHandler"
      />
    </modal>
  </div>
</template>


<script>
  import CfModifierMenuItemChild from '@/view/components/globals/cf-modifier-menu-item-child/index.vue';


  export default {
    name: 'CfModifierPortionGroup',

    components: {
      CfModifierMenuItemChild
    },

    inject: {
      $validator: {
        from: '$validator'
      },

      validatedForm: {
        from: 'validatedForm'
      }
    },

    props: {
      hidePrices: {
        type: Boolean,
        default: false
      },
      label: {
        type: String,
        required: true
      },
      modifierGroup: {
        type: Object,
        required: true
      },
      name: {
        type: String,
        required: true
      },
      readOnly: {
        type: Boolean,
        default: false
      },
      selectedModifiers: {
        type: Array,
        default: null
      }
    },

    data() {
      return {
        portionTypes: [],
        selectedPortionType: null,
        selections: {},
        showInvalidState: false
      };
    },

    computed: {
      modalName() {
        return `${this.name}-menuItemChild`;
      }
    },

    watch: {
      selectedPortionType: 'selectionsChangeHandler',
      selections: {
        handler: 'selectionsChangeHandler',
        deep: true
      }
    },

    created() {
      this.createdHandler();
    },

    unmounted() {
      this.destroyedHandler();
    },

    methods: {
      createdHandler() {
        this.parseModifierGroup();

        this.validatedForm.addValidator({
          name: this.name,
          validate: this.validate
        });
      },

      destroyedHandler() {
        this.validatedForm.removeValidator(this.name);
      },

      editPortion(modifier) {
        const existingSelection = this.selections[modifier.menuItemId];

        this.$openModal(this.modalName, {
          item: modifier,
          selectedModifiers: existingSelection ? existingSelection.modifiers : null,
          selectedQuantity: existingSelection ? existingSelection.quantity : null
        });
      },

      parseModifierGroup() {
        const portionTypes = [];

        this.modifierGroup.modifiers.forEach((modifier) => {
          const portionType = portionTypes.find(p => p.modifierTemplate === modifier.modifierTemplate);

          if (!portionType) {
            portionTypes.push({
              modifiers: [modifier],
              modifierIds: [modifier.menuItemId],
              modifierTemplate: modifier.modifierTemplate
            });
          }

          else {
            portionType.modifiers.push(modifier);
            portionType.modifierIds.push(modifier.menuItemId);
          }
        });

        this.portionTypes = portionTypes;

        this.parseModifierGroupSelections();
      },

      parseModifierGroupSelections() {
        if (this.selectedModifiers) {
          this.selectedModifiers.forEach((selectedModifier) => {
            const portionType = this.portionTypes.find(pt => pt.modifierIds.includes(selectedModifier.menuItemId));

            if (portionType) {
              this.selectPortionType(portionType);
              this.selections[selectedModifier.menuItemId] = {
                modifiers: selectedModifier.modifiers || [],
                quantity: selectedModifier.quantity
              };
            }
          });
        }

        else {
          this.selectPortionType(this.portionTypes[0]);
        }
      },

      returnSelectedModifiers(modifier, selection) {
        let allMenuModifiers = [];
        const result = {
          modifiers: [],
          selectionDisplay: '',
          totalPrice: modifier.price
        };

        if (!selection) {
          return result;
        }

        modifier.modifierGroups.forEach((group) => {
          allMenuModifiers = allMenuModifiers.concat(group.modifiers);
        });

        selection.modifiers.forEach((selectedModifier) => {
          const menuModifier = allMenuModifiers.find(m => m.menuItemId === selectedModifier.menuItemId);

          if (menuModifier) {
            result.selectionDisplay += `${menuModifier.description}, `;
            result.totalPrice += menuModifier.price * selectedModifier.quantity;
            result.modifiers.push(menuModifier);
          }
        });

        result.selectionDisplay = result.selectionDisplay.replace(/,\s$/, '');

        return result;
      },

      selectionsChangeHandler() {
        const selectedModifiers = [];

        if (!this.selectedPortionType) {
          return;
        }

        this.selectedPortionType.modifiers.forEach((modifier) => {
          const selection = this.selections[modifier.menuItemId];

          if (selection) {
            selectedModifiers.push({
              menuItemId: modifier.menuItemId,
              modifiers: selection.modifiers,
              quantity: selection.quantity
            });
          }
        });

        this.$emit('selected', {
          modifierGroupId: this.modifierGroup.modifierGroupId,
          modifiers: selectedModifiers
        });
      },

      selectPortionType(type) {
        this.selectedPortionType = {
          ...type
        };
      },

      updatedChildHandler(child) {
        this.updateSelection(child.item.menuItemId, {
          modifiers: child.modifiers,
          quantity: child.quantity
        });

        this.$closeModal(this.modalName);
      },

      updateSelection(menuItemId, value) {
        this.selections[menuItemId] = value;

        /*
          NOTE: The following change event dispatch will trigger the "dirty" handler for
          <validate-form>, which will unset the favorite button if the item was a favorite.
        */

        const event = new Event('change', {
          bubbles: true
        });

        this.$el.dispatchEvent(event);
      },

      validate() {
        this.showInvalidState = true;
        return this.validateSelectedPortionType();
      },

      validateSelectedPortionType() {
        if (!this.selectedPortionType) {
          return false;
        }

        const results = this.selectedPortionType.modifiers.map(modifier => this.validatePortion(modifier));

        const isValid = !results.includes(false);

        return isValid;
      },

      validatePortion(modifier) {
        return !!this.selections[modifier.menuItemId];
      }
    }
  };
</script>
